import { useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Form, Button, Stack, Alert, Spinner } from "react-bootstrap";
import LoggedInContainer from "../../layout/LoggedInContainer";
import { getDeckRecordsForConsignmentDay, setDeckRecordsDB, getPadDepthOptions } from "../../api/apiAccess";
import { DetailedDeckRecord } from "../../classes/DetailedDeckRecord";
import { ManurePadDepthType } from "../../classes/ManurePadDepthType";
import ManurePadScores from "../../components/DeckRecords/ManurePadScores";
import { sortArrayInStandardDeckOrder } from "../../utils/ListUtils";
import '../../css/deckrecords.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { getEditPageReasons } from "../../api/apiAccess";
import EditLogModal from "../../components/EditLogModal";
import { UserContext } from "../..";
import ReadWriteButton from "../../components/ReadWriteElements/ReadWriteButton";
import ReadWriteFormCheck from "../../components/ReadWriteElements/ReadWriteFormCheck";
import { isReportEditable } from "../../utils/common";

// See comment in DeckRecord.js in the classes folder in regards to empty decks
const Edit_Deck_Record_Manure_Pad_Scores = () => {
    let { state } = useLocation();
    const inputFormContainer = useRef();
    const navigate = useNavigate();
    const userCtx = useContext(UserContext);

    const [isLoading, setIsLoading] = useState(true);
    const [hasData, setHasData] = useState(false);
    const [deckRecords, setDeckRecords] = useState([]);
    const [hasSaveError, setHasSaveError] = useState(false);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [triggerShowInvalid, setTriggerShowInvalid] = useState(-1);
    const [padDepthOptions, setPadDepthOptions] = useState([]);
    const [editModalShow, setEditModalShow] = useState(false);
    const [editPageReasons, setEditPageReasons] = useState([]);

    const groupByDecks = (detailedDeckRecords) => {
        let lGrouped = [];

        detailedDeckRecords.forEach(dr => {
            let lGroup = lGrouped.find(gr => gr.deckID === dr.DeckRecord.consignmentDeckID)
            if (!lGroup) {
                let lNewDeck = { deckID: dr.DeckRecord.consignmentDeckID, name: dr.ConsignmentDeck.deckName, isBridge: dr.ConsignmentDeck.isBridge, detailedDeckRecords: [] }
                lGroup = lNewDeck;
                lGrouped.push(lNewDeck);
            }
            // on load data should be valid
            dr.DeckRecord.isValid = true;
            lGroup.detailedDeckRecords.push(dr);
        })

        // sort decks
        lGrouped = sortArrayInStandardDeckOrder(lGrouped, "name", "isBridge");

        return lGrouped;
    };

    useEffect(() => {
        const fetchPageData = async () => {
            let lPromises = [
                getEditPageReasons(),
                getDeckRecordsForConsignmentDay(state.consignmentDay.id),
                getPadDepthOptions()
            ];

            Promise.all(lPromises)
                .then(response => {
                    if (response && response.every(resp => resp.status === 200)) {

                        let reasons = response[0].data;
                        setEditPageReasons(reasons);

                        let detailedDeckRecords = [];
                        response[1].data.forEach(d => {
                            // set to empty string to prevent js error when converting from uncontrolled to controlled input
                            if (d.deckRecord.manurePadAverageDepthID === null) {
                                d.deckRecord.manurePadAverageDepthID = "";
                            }
                            detailedDeckRecords.push(new DetailedDeckRecord(d));
                        })

                        let lGrouped = groupByDecks(detailedDeckRecords);

                        setDeckRecords(lGrouped);

                        let lOptions = [];

                        // add empty option
                        lOptions.push(new ManurePadDepthType("", ""));

                        response[2].data.forEach(d => {
                            lOptions.push(new ManurePadDepthType(d));
                        })

                        setPadDepthOptions(lOptions);

                        return true;
                    }
                    else {
                        return false;
                    }
                }).then(result => {
                    setHasData(result);
                    setHasLoadError(!result)
                    setIsLoading(false);
                })
                .catch(error => {
                    setHasLoadError(true);
                    setIsLoading(false);
                })
        }

        fetchPageData();
    }, []);

    const onUpdateSpeciesRecord = (deckID, deckRecordID, percentPadAbsent, percentDry, percentSloppy, avgPadDepthID, isValid) => {
        let lUpdatedRecords = [...deckRecords];
        let lDeckGroupToUpdate = lUpdatedRecords.find(deck => deck.deckID === deckID);
        if (lDeckGroupToUpdate) {
            let lRecordToUpdate = lDeckGroupToUpdate.detailedDeckRecords.find(gr => gr.DeckRecord.id === deckRecordID);

            if (lRecordToUpdate) {
                if (lRecordToUpdate.DeckRecord.isEmpty === false) {
                    lRecordToUpdate.DeckRecord.isValid = isValid;
                    lRecordToUpdate.DeckRecord.manurePadAbsentPercent = percentPadAbsent;
                    lRecordToUpdate.DeckRecord.manurePadDryPercent = percentDry;
                    lRecordToUpdate.DeckRecord.manurePadSloppyPercent = percentSloppy;
                    lRecordToUpdate.DeckRecord.manurePadAverageDepthID = avgPadDepthID;
                    setDeckRecords(lUpdatedRecords);
                }
            }
        }
    }

    const onActionTakenChange = (deckID, washedDown, addedBedding, shovelledOut) => {
        let lUpdatedRecords = [...deckRecords];
        let lNoActionTaken = getNoActionTakenStatus(deckID, washedDown, addedBedding, shovelledOut);

        let lDeck = lUpdatedRecords.find(d => d.deckID === deckID);

        // Action taken is on a per deck basis, so we need to set the value for all species on this deck
        // Even if the deck is empty the action still applied to the deck so we should set it
        lDeck.detailedDeckRecords.forEach(dr => {
            dr.DeckRecord.deckWashedDown = washedDown;
            dr.DeckRecord.addedBedding = addedBedding;
            dr.DeckRecord.shovelledOut = shovelledOut;
            dr.DeckRecord.noActionTaken = lNoActionTaken;
        })

        setDeckRecords(lUpdatedRecords);
    }

    const getNoActionTakenStatus = (deckID, washedDown, addedBedding, shovelledOut) => {
        let lDeck = deckRecords.find(d => d.deckID === deckID);

        if (lDeck.detailedDeckRecords.some(dr => dr.DeckRecord.manurePadSloppyPercent && dr.DeckRecord.manurePadSloppyPercent > 0)) {
            if ((washedDown === false || !washedDown) && (addedBedding === false || !addedBedding) && (shovelledOut === false || !shovelledOut)) {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            return null;
        }
    }


    const goBack = () => {
        navigate(`/viewhistorydata/${state.consignmentDay.consignmentId}`, { state: { focusedTab: "manure pad score", selectedColumnIndex: state?.selectedColumnIndex, editable: isReportEditable(userCtx, state.allowEditing) } })
    }

    const writeToDB = async () => {
        // should already be valdiated at this point
        let lRecordsToSave = [];

        for (const deck of deckRecords) {
            for (const deckRecord of deck.detailedDeckRecords) {
                if (deckRecord.DeckRecord.manurePadAverageDepthID === "") {
                    deckRecord.DeckRecord.manurePadAverageDepthID = null;
                }
                lRecordsToSave.push(deckRecord.DeckRecord);
            }
        }

        let result = await setDeckRecordsDB(lRecordsToSave);
        if (result.status === 200) {
            navigate(`/viewhistorydata/${state.consignmentDay.consignmentId}`, { state: { focusedTab: "manure pad score", selectedColumnIndex: state?.selectedColumnIndex, editable: isReportEditable(userCtx, state.allowEditing) } });
            // setShowSaveSuccess(true);
            // setHasSaveError(false);
            // setTimeout(() => {
            //     setShowSaveSuccess(false);
            // }, 15000);
        }
        else {
            setShowSaveSuccess(false);
            setHasSaveError(true);
        }
    }

    const validateAndShowEditModal = async () => {
        // Validate
        let lIsValid = true;
        deckRecords.forEach(deck => {
            deck.detailedDeckRecords.forEach(dr => {
                if (dr.DeckRecord.isValid === false) {
                    lIsValid = false;
                }
            })
        })

        if (lIsValid === true) {
            setEditModalShow(true);
        }
        else {
            // add invalid style to input controls
            setTriggerShowInvalid(triggerShowInvalid + 1);
            window.scrollTo(0, inputFormContainer.current.offsetTop);
        }
    }

    const onEditModalClosed = () => {
        setEditModalShow(false);
    }

    const onEditModalSaveSuccess = async () => {
        setEditModalShow(false);
        await writeToDB();
    }

    const onEditModalSaveFailed = () => {
        // Show logging error
    }

    return (
        <LoggedInContainer>
            <Container fluid className="p-0" style={{ backgroundColor: "#F5F5F5" }}>
                <Row className="upper-panel mx-0">
                    <Container>
                        <Row>
                            <Col xs={12}>
                                <div className="text-center heading-text mb-2 mt-3" style={{ color: "#FFF", paddingTop: "56px" }}>
                                    <div style={{ lineHeight: "53px", fontSize: "35px" }}>
                                        {isReportEditable(userCtx, state.allowEditing) ? "Edit Deck Records - Manure Pad Scores" : "View Deck Records - Manure Pad Scores"}
                                    </div>
                                    <div><em>Day {state.consignmentDay.dayNumber}</em></div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </Row>

                <Container ref={inputFormContainer} className="py-4 px-5 form-container-full">
                    {
                        isLoading ?
                            <Row>
                                <Col>
                                    <div className="text-center pt-5">
                                        <Spinner className="mx-auto" />
                                        <div className="mx-auto">Loading data...</div>
                                    </div>
                                </Col>
                            </Row>
                            : null
                    }
                    {hasData && isLoading === false ?
                        <Row>
                            <Col xs={12} lg={12}>
                                <Row className="align-items-end">
                                    <Col xs={2}></Col>
                                    <Col xs={6}>
                                        <Row className="align-items-end">
                                            <Col xs={3} className="dr-column-heading">
                                                <div>Species</div>
                                            </Col>
                                            <Col xs={9}>
                                                <Row className="align-items-end">
                                                    <Col xs={3} className="dr-column-heading align-bottom">
                                                        <Stack direction="horizontal" className="align-items-end">
                                                            <div>Pad Absent</div>
                                                            <div><FontAwesomeIcon className="info-icon" icon={faInfoCircle} title={"Deck level by % of deck during morning rounds.\nTypically seen at the start of a voyage or after a deck wash down. If most of the flooring in a pen is visible and not covered by a layer of bedding, manure solids or slurry, it will be scored as pad not present."}></FontAwesomeIcon></div>
                                                        </Stack>
                                                    </Col>
                                                    <Col xs={3} className="dr-column-heading align-bottom">
                                                        <Stack direction="horizontal" className="align-items-end">
                                                            <div>Pad Present & Dry, Firm, Tacky</div>
                                                            <div><FontAwesomeIcon className="info-icon" icon={faInfoCircle} title={"Deck level by % of deck during morning rounds.\nThe manure pad holds form and provides traction and cushioning for the livestock. Where there is very little moisture in the manure pad, it will be dry and crumbly, but as the moisture content increases, it will become firm and possibly tacky. There will be minimal coat or fleece contamination when the manure pad is dry, firm or tacky."}></FontAwesomeIcon></div>
                                                        </Stack>
                                                    </Col>
                                                    <Col xs={3} className="dr-column-heading align-bottom">
                                                        <Stack direction="horizontal" className="align-items-end">
                                                            <div>Pad Present & Moist, Sloppy</div>
                                                            <div><FontAwesomeIcon className="info-icon" icon={faInfoCircle} title={"Deck level by % of deck during morning rounds.\nHigh moisture content will make the manure pad deteriorate to become sloppy or have the consistency of slurry. The pad is no longer compressible and will not hold form. Livestock hooves will sink to the flooring and the manure will readily cause contamination of their coats or fleeces."}></FontAwesomeIcon></div>
                                                        </Stack>
                                                    </Col>
                                                    <Col xs={3} className="dr-column-heading align-bottom">
                                                        <div>Pad Avg Depth</div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col xs={4} className="dr-column-heading align-bottom">
                                        <Stack direction="horizontal" className="align-items-end">
                                            <div style={{ display: "inline" }}>Deck Action Taken</div>
                                            <div><FontAwesomeIcon className="info-icon" icon={faInfoCircle} title='Please note that if you do not select any actions below, the system will record this as "No action taken" if the pad is sloppy.'></FontAwesomeIcon></div>
                                        </Stack>
                                    </Col>
                                </Row>
                                {
                                    deckRecords.map((deck, deckIndex) => {
                                        // actions should only be enabled if at least one species on the deck is not empty
                                        let lAllEmpty = deck.detailedDeckRecords.every(dr => dr.DeckRecord.isEmpty === true);
                                        let lWashedDown = deck.detailedDeckRecords[0].DeckRecord.deckWashedDown;
                                        let lShovelledOut = deck.detailedDeckRecords[0].DeckRecord.shovelledOut;
                                        let lAddedBedding = deck.detailedDeckRecords[0].DeckRecord.addedBedding;

                                        return (
                                            <Row key={`deck${deck.deckID}`} className={`pb-3 pt-3 gray-border-top`} >
                                                <Col xs={2} className="dr-text">
                                                    {deck.name}
                                                </Col>
                                                <Col xs={6}>
                                                    {
                                                        deck.detailedDeckRecords.map((dr, drIndex) => {
                                                            return (
                                                                <ManurePadScores key={`manurePadScores${dr.Species.id}`} deckRecord={dr} onUpdate={onUpdateSpeciesRecord}
                                                                    hasPadding={drIndex !== deck.detailedDeckRecords.length - 1} onTriggerValidation={triggerShowInvalid}
                                                                    padDepthOptions={padDepthOptions} />
                                                            )
                                                        })
                                                    }
                                                </Col>
                                                <Col xs={4}>
                                                    <Row>
                                                        <Col xs={6}>
                                                            <ReadWriteFormCheck disabled={lAllEmpty} type="checkbox" labelstacked={false} className="text-13-400 secondary-grey" label="Deck washed down" checked={lWashedDown} onChange={(e) => onActionTakenChange(deck.deckID, e.target.checked, lAddedBedding, lShovelledOut)} />
                                                        </Col>
                                                        <Col xs={6}>
                                                            {

                                                                deck.detailedDeckRecords.some(dr => dr.DeckRecord.manurePadSloppyPercent > 0) ?
                                                                    <Stack>
                                                                        <ReadWriteFormCheck type="checkbox" labelstacked={false} className="text-13-400 secondary-grey" label="Added bedding" checked={lAddedBedding} onChange={(e) => onActionTakenChange(deck.deckID, lWashedDown, e.target.checked, lShovelledOut)} />
                                                                        <ReadWriteFormCheck type="checkbox" labelstacked={false} className="text-13-400 secondary-grey" label="Shovelled out" checked={lShovelledOut} onChange={(e) => onActionTakenChange(deck.deckID, lWashedDown, lAddedBedding, e.target.checked)} />
                                                                    </Stack>
                                                                    : null
                                                            }

                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        )
                                    })
                                }
                            </Col>
                        </Row>
                        : null
                    }
                </Container>
                {
                    isLoading === false && hasData === true ?
                        <Row className="w-100 pt-4" style={{ position: "relative", top: "-300px" }}>
                            {
                                showSaveSuccess === true ?
                                    <Col xs={12}>
                                        <Container style={{ width: "874px" }}>
                                            <Row>
                                                <Col xs={12}>
                                                    <Alert variant='success'>Deck record updated successfully.</Alert>
                                                </Col>
                                            </Row>
                                        </Container>
                                    </Col>
                                    : null
                            }

                            {
                                hasSaveError === true ?
                                    <Col xs={12}>
                                        <Container>
                                            <Row>
                                                <Col>
                                                    <Alert variant='danger'>Unable to save. Please wait a few minutes an try again. If the problem persists contact administration.</Alert>
                                                </Col>
                                            </Row>
                                        </Container>
                                    </Col>
                                    : null
                            }
                            <EditLogModal
                                title="Edit manure pad score data"
                                page="Manure Pad Scores"
                                userID={userCtx.user.id}
                                consignmentIDs={[state.consignmentDay.consignmentId]}
                                showModal={editModalShow}
                                reasonOptions={editPageReasons}
                                onClose={onEditModalClosed}
                                onSaveSuccess={onEditModalSaveSuccess}
                                onSaveFail={onEditModalSaveFailed}
                            />
                            <Col xs={12}>
                                <Stack direction="horizontal" gap={2} style={{ justifyContent: "center" }}>
                                    <ReadWriteButton writeonly={false} readonlytext="Back" variant="secondary" onClick={() => { goBack() }} size="lg" style={{ minWidth: "150px" }}>Cancel</ReadWriteButton>
                                    {isReportEditable(userCtx, state.allowEditing) &&
                                        <ReadWriteButton writeonly={true} variant="primary" onClick={() => { validateAndShowEditModal() }} size="lg" style={{ minWidth: "150px" }}>Update</ReadWriteButton>
                                    }
                                </Stack>
                            </Col>
                        </Row>
                        : null
                }
                {
                    (hasLoadError === true || hasData === false) && isLoading === false ?
                        <Container>
                            <Row>
                                <Col xs={12}>
                                    <Alert variant='danger'>Unable to load deck record.</Alert>
                                </Col>
                                <Col xs={12}>
                                    <Button className="me-2 ms-auto" variant="primary" onClick={() => { goBack() }} size="lg" style={{ minWidth: "150px" }}>Back</Button>
                                </Col>
                            </Row>
                        </Container>

                        : null
                }
            </Container >
        </LoggedInContainer >
    )
};

export default Edit_Deck_Record_Manure_Pad_Scores;